/**
 * Mask Optics VR PDP — prescription flow for safety/mask eyewear.
 *
 * Extends the xreal-style WC foundation with:
 *   - <pdp-mask-page>        root: loads product + lens product + framesByGroup
 *                            + derives the temple type from product tags.
 *   - <pdp-frame-temple-type> toggle between temple variants (e.g. skul / cable).
 *                            Selecting a type filters the colour swatcher and
 *                            jumps to the first compatible frame.
 *   - <pdp-frame-sizes>       simple variant-grid selector for frame sizes.
 *
 * Generic elements (title, price, gallery, sticky-cart, product-options,
 * add-to-cart, frame-color-switcher, lens-options) come from pdp-core.
 *
 * Alpine prescription popup and bundled checkout stay in place exactly
 * as in the xreal template — the section liquid wraps rx-form with
 * `heroMaskOpticsVr` and listens for `pdp:open-prescription`.
 */
import { PdpBaseComponent, PdpUtils } from 'pdp-core';

// ============================================================================
// <pdp-mask-page> — page root
// ============================================================================

class PdpMaskPage extends PdpBaseComponent {
  init() {
    this.loadInitialData();
    this.recomputeTotal();
    this.maybeLoadShopProducts();
  }

  setupListeners() {
    this.listen(this, 'pdp:change-variant', (e) => this.handleChangeVariant(e.detail));
    this.listen(this, 'pdp:change-lens-variant', (e) => this.handleChangeLensVariant(e.detail));
    this.listen(this, 'pdp:change-frame-product', (e) => this.handleChangeFrameProduct(e.detail));
    this.listen(this, 'pdp:change-temple-type', (e) => this.handleChangeTempleType(e.detail));
    this.listen(this, 'pdp:add-to-cart', () => this.handleAddToCart());

    // Keep the single totalPrice field in sync so every price-aware component
    // (main price, ATC, sticky cart) reflects frame + lens as one number.
    this.onStateChange('selectedVariant', () => this.recomputeTotal());
    this.onStateChange('rxLensSelectedVariant', () => this.recomputeTotal());
  }

  recomputeTotal() {
    const main = this.state.get('selectedVariant');
    const lens = this.state.get('rxLensSelectedVariant');
    if (!main) return;

    const total = (main.price || 0) + (lens?.price || 0);
    const mainCompare = main.compare_at_price || main.price || 0;
    const lensCompare = lens?.compare_at_price || lens?.price || 0;
    const totalCompare = mainCompare + lensCompare;
    const hasDiscount = totalCompare > total;

    this.state.setState({
      totalPrice: total,
      totalCompareAtPrice: hasDiscount ? totalCompare : null,
    });
  }

  loadInitialData() {
    const productEl = this.querySelector('[data-pdp-product-data]');
    const lensEl = this.querySelector('[data-pdp-lens-product-data]');
    if (!productEl) { console.warn('[pdp-mask] missing [data-pdp-product-data]'); return; }

    try {
      const { product, productOptions, selectedVariant } = JSON.parse(productEl.textContent);
      const updates = {
        product,
        productOptions: productOptions || [],
        selectedVariant: selectedVariant || product.variants[0],
        images: PdpUtils.buildImages(product),
        frameTag: PdpUtils.getFrameTagByProduct(product),
        frameColor: PdpUtils.getFrameColorByProduct(product),
        frameGroup: PdpUtils.getFrameGroupByProduct(product),
        activeTemple: PdpUtils.getTempleTypeByProduct(product),
      };

      if (lensEl) {
        const lens = JSON.parse(lensEl.textContent);
        updates.rxLensProduct = lens.product;
        updates.rxLensProductOptions = lens.productOptions || [];
        updates.rxLensSelectedVariant = lens.selectedVariant || lens.product?.variants?.[0] || null;
      }

      this.state.setState(updates);
    } catch (e) {
      console.error('[pdp-mask] failed to parse product data', e);
    }
  }

  async maybeLoadShopProducts() {
    try {
      const res = await fetch('https://cdn.eazyf.com/foc/json/products_frame.json', { signal: this.signal });
      const shopProducts = await res.json();

      const groups = new Map();
      Object.values(shopProducts).forEach((p) => {
        const tag = PdpUtils.getFrameTagByProduct(p);
        if (!tag) return;
        const group = tag.split('~')[0].split('_')[1];
        if (!groups.has(group)) groups.set(group, p);
      });
      const framesByGroup = Array.from(groups).map(([group, first]) => ({
        frameGroup: group,
        defaultFrameGroupProduct: first,
        image: first.media?.[0]?.src || null,
        allFrames: Object.values(shopProducts).filter((p) => {
          const tag = PdpUtils.getFrameTagByProduct(p);
          return tag && tag.split('~')[0].split('_')[1] === group;
        }),
      }));

      this.state.setState({ shopProducts, framesByGroup });
    } catch (e) {
      if (e.name !== 'AbortError') console.warn('[pdp-mask] shopProducts fetch failed', e);
    }
  }

  handleChangeVariant({ optionName, value }) {
    const product = this.state.get('product');
    const selected = this.state.get('selectedVariant');
    if (!product || !selected) return;
    const option = this.state.get('productOptions').find((o) => o.name === optionName);
    if (!option) return;
    const newOptions = [...selected.options];
    newOptions[option.position - 1] = value;
    const next = PdpUtils.findVariantByOptions(product, newOptions);
    if (next) this.state.set('selectedVariant', next);
  }

  handleChangeLensVariant({ optionName, value }) {
    const lensProduct = this.state.get('rxLensProduct');
    const selected = this.state.get('rxLensSelectedVariant');
    if (!lensProduct || !selected) return;
    const option = this.state.get('rxLensProductOptions').find((o) => o.name === optionName);
    if (!option) return;
    const newOptions = [...selected.options];
    newOptions[option.position - 1] = value;
    const next = PdpUtils.findVariantByOptions(lensProduct, newOptions);
    if (next) this.state.set('rxLensSelectedVariant', next);
  }

  handleChangeFrameProduct({ product }) {
    if (!product) return;
    this.state.setState({
      product,
      productOptions: product.productOptions || product.options_with_values || [],
      selectedVariant: product.variants?.[0],
      images: PdpUtils.buildImages(product),
      frameTag: PdpUtils.getFrameTagByProduct(product),
      frameColor: PdpUtils.getFrameColorByProduct(product),
      frameGroup: PdpUtils.getFrameGroupByProduct(product),
    });
    if (product.url) history.replaceState(null, '', product.url);
  }

  // When the customer taps skul/cable, re-filter frames and jump to the first
  // colour that matches that temple type — otherwise the visible swatch list
  // might no longer include the current frame.
  handleChangeTempleType({ templeType }) {
    this.state.set('activeTemple', templeType);
    const framesByGroup = this.state.get('framesByGroup');
    const frameGroup = this.state.get('frameGroup');
    const group = framesByGroup.find((g) => g.frameGroup === frameGroup);
    const match = (group?.allFrames || []).find((p) =>
      PdpUtils.productMatchesTempleType(p, templeType)
    );
    if (match && PdpUtils.getFrameTagByProduct(match) !== this.state.get('frameTag')) {
      this.handleChangeFrameProduct({ product: match });
    }
  }

  // ATC always opens the prescription popup (Alpine bridge), identical to xreal.
  // Pass the live variants so the Alpine side bundles the right line items.
  handleAddToCart() {
    window.dispatchEvent(new CustomEvent('pdp:open-prescription', {
      detail: {
        variant: this.state.get('selectedVariant'),
        lensVariant: this.state.get('rxLensSelectedVariant'),
      },
    }));
  }
}

// ============================================================================
// <pdp-frame-temple-type> — skul / cable toggle
// ============================================================================

class PdpFrameTempleType extends PdpBaseComponent {
  init() { this.render(); }

  setupListeners() {
    this.onStateChange('activeTemple', () => this.render());
    this.onStateChange('frameGroup', () => this.render());
    this.onStateChange('shopProducts', () => this.render());

    this.listen(this, 'click', (e) => {
      const btn = e.target.closest('[data-pdp-temple-type]');
      if (!btn) return;
      this.emit('pdp:change-temple-type', { templeType: btn.dataset.pdpTempleType });
    });
  }

  render() {
    const root = this.querySelector('[data-pdp-temple-root]');
    if (!root) return;

    const active = this.state.get('activeTemple');
    const group = this.state.get('frameGroup');
    const shop = this.state.get('shopProducts') || {};
    const shopList = Object.values(shop);

    const options = JSON.parse(this.getAttribute('data-options') || '[]');

    // Only show option if at least one frame in the group supports it.
    const available = options.filter((o) =>
      shopList.some((p) =>
        (p.tags || []).includes(`temple-type_${group}~${o.value}`)
      )
    );

    if (available.length < 2) {
      this.toggleAttribute('hidden', true);
      return;
    }
    this.toggleAttribute('hidden', false);

    const esc = PdpUtils.escapeHtml;
    root.innerHTML = available.map((o) => `
      <button
        type="button"
        class="pdp-tag${active === o.value ? ' pdp-tag--active' : ''}"
        data-pdp-temple-type="${esc(o.value)}"
      >
        <span class="pdp-tag__value">${esc(o.label)}</span>
      </button>
    `).join('');
  }
}

// ============================================================================
// <pdp-frame-sizes> — size/variant picker for the main product
// ============================================================================

class PdpFrameSizes extends PdpBaseComponent {
  init() { this.render(); }

  setupListeners() {
    this.onStateChange('product', () => this.render());
    this.onStateChange('selectedVariant', () => this.render());

    this.listen(this, 'click', (e) => {
      const btn = e.target.closest('[data-pdp-variant-id]');
      if (!btn) return;
      const id = btn.dataset.pdpVariantId;
      const variant = this.state.get('product')?.variants?.find((v) => String(v.id) === String(id));
      if (variant) this.state.set('selectedVariant', variant);
    });
  }

  render() {
    const root = this.querySelector('[data-pdp-sizes-root]');
    const product = this.state.get('product');
    const selected = this.state.get('selectedVariant');
    if (!root || !product || !selected) return;

    // Hide whenever there are no meaningful size variants — either the
    // product has just the auto-generated default, or every non-custom
    // variant is still titled "Default Title".
    const hasRealVariants = (product.variants || [])
      .filter((v) => !(v.title || '').toLowerCase().includes('custom size'))
      .some((v) => v.title !== 'Default Title');
    if (product.has_only_default_variant || !hasRealVariants) {
      this.toggleAttribute('hidden', true);
      return;
    }
    this.toggleAttribute('hidden', false);

    const esc = PdpUtils.escapeHtml;
    root.innerHTML = (product.variants || []).map((v) => {
      const active = v.id === selected.id;
      const title = esc(v.title).replace('(', '<br>(');
      return `
        <button
          type="button"
          class="pdp-tag${active ? ' pdp-tag--active' : ''}"
          data-pdp-variant-id="${v.id}"
        ><span class="pdp-tag__value">${title}</span></button>
      `;
    }).join('');
  }
}

// ============================================================================
// REGISTER
// ============================================================================

if (!customElements.get('pdp-mask-page')) {
  customElements.define('pdp-mask-page', PdpMaskPage);
}
if (!customElements.get('pdp-frame-temple-type')) {
  customElements.define('pdp-frame-temple-type', PdpFrameTempleType);
}
if (!customElements.get('pdp-frame-sizes')) {
  customElements.define('pdp-frame-sizes', PdpFrameSizes);
}
